import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import SearchToolbar from "../SearchToolbar/index.js";
import { useI18n, t } from "@bookingcom/lingojs-react";
import useLocaleContext from "../../../hooks/useLocaleContext.js";
import useSearchbox from "../../../hooks/useSearchbox.js";
import { Container, HiddenVisually } from "@bookingcom/bui-react";
import { SearchboxController, SearchBoxVerticalCollapsible } from "@bookingcom/flights-searchbox";
import styles from "./SearchHeader.module.css";
import { useStickySearchBoxExp } from "../../../utils/experiments/mdotvalue/flights_mdotvalue_sr_sticky_search_box.js";
import useUserAgent from "../../../hooks/useUserAgent.js";
import throttle from "../../../utils/throttle.js";
import { mcn } from "../../../utils/mergeClassnames.js";
import { useLocation } from "react-router-dom";
import { useSpringDeal } from "../../../hooks/useSpringDeal.js";
import { useSummerDeal } from "../../../hooks/useSummerDeal.js";
import { trackWithDefaultStage } from "@flights/et-universal";
import useGlobalContext from "../../../hooks/useGlobalContext.js";
import useFareCalendarEligible from "../../../hooks/useFareCalendarEligible.js";
const SearchHeader = ({ hideToolbar = false, onSearch, availableCountries, routeName }) => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location?.search);
    const i18n = useI18n();
    const { isRTL } = useLocaleContext();
    const searchBoxProps = useSearchbox();
    const datepickerOpen = !!Number(queryParams.get("open_datepicker"));
    const searchBoxControllerProps = {
        ...searchBoxProps,
        isDateDropdownOpen: datepickerOpen
    };
    const { isMobile } = useUserAgent();
    const searchHeaderRef = useRef(null);
    const [isSticky, setIsSticky] = useState(false);
    const collapseSearchBox = useRef();
    const { getVariant, trackFlightsSearchBoxOpen } = useStickySearchBoxExp(isSticky);
    const { isSpringDealExp } = useSpringDeal();
    const { isSummerDealExp } = useSummerDeal();
    const { currencyCode } = useGlobalContext();
    const isFareCalendarEligible = useFareCalendarEligible();
    const isStickySearchBoxExpRunning = getVariant() > 0;
    const Wrapper = useMemo(() => {
        return forwardRef(wrapper);
        function wrapper({ children, ...props }, ref) {
            return (React.createElement(React.Fragment, null,
                React.createElement("div", { ref: ref }),
                isStickySearchBoxExpRunning ? React.createElement("div", { ...props }, children) : children));
        }
    }, [isStickySearchBoxExpRunning]);
    useEffect(() => {
        if (!isMobile)
            return;
        const scrollHandler = throttle(() => {
            const element = searchHeaderRef.current;
            const top = element?.getBoundingClientRect().top;
            const isPastSearchHeader = top !== undefined && top <= 0;
            setIsSticky(isPastSearchHeader);
        }, 100);
        addEventListener("scroll", scrollHandler);
        return () => {
            removeEventListener("scroll", scrollHandler);
        };
    }, [isMobile]);
    useEffect(() => {
        if (isSticky && collapseSearchBox.current && isStickySearchBoxExpRunning) {
            collapseSearchBox.current();
        }
    }, [isSticky, isStickySearchBoxExpRunning]);
    const onExpand = useCallback(() => {
        trackFlightsSearchBoxOpen();
        if (isStickySearchBoxExpRunning && isSticky) {
            scrollTo(0, 0);
        }
    }, [isStickySearchBoxExpRunning, isSticky, trackFlightsSearchBoxOpen]);
    const setCollapseFn = useCallback((collapseFn) => {
        if (isStickySearchBoxExpRunning) {
            collapseSearchBox.current = collapseFn;
        }
    }, [isStickySearchBoxExpRunning]);
    const verticalCollapsibleSearchbox = useCallback(() => (React.createElement(SearchBoxVerticalCollapsible, { onExpand: onExpand, setCollapseFn: setCollapseFn, isMobile: isMobile })), [onExpand, setCollapseFn, isMobile]);
    return (React.createElement(Wrapper, { className: mcn(styles.searchHeader, isStickySearchBoxExpRunning ? styles.searchHeaderSticky : "", isStickySearchBoxExpRunning && isSticky ? styles.searchHeaderIsStuck : ""), ref: searchHeaderRef },
        React.createElement("div", { className: styles.minifiedSearchBox },
            React.createElement("div", { className: styles.background }),
            React.createElement(HiddenVisually, null,
                React.createElement("h1", null, i18n.trans(t("a11y_flights_sr_search_hidden_heading")))),
            React.createElement(Container, null,
                React.createElement(SearchboxController, { i18n: i18n, ...searchBoxControllerProps, isRTL: isRTL, onSearch: (searchParams) => {
                        if (onSearch && typeof onSearch === "function") {
                            onSearch(searchParams);
                        }
                        else {
                            searchBoxProps.onSearch(searchParams);
                        }
                    }, availableCountries: availableCountries, routeName: routeName, enabledCountriesAndAnywhere: true, isSpringDealExp: isSpringDealExp, isSummerDealExp: isSummerDealExp, isSearchAnyCityEnabled: true, fareCalendarConfig: {
                        isFareCalendarMobileEnabled: isFareCalendarEligible &&
                            (!!trackWithDefaultStage("flights_web_cat_fare_calendar_oneway", 1) ||
                                !!trackWithDefaultStage("flights_web_cat_fare_calendar_data_collection", 1)),
                        isFareCalendarDisplayEnabled: isFareCalendarEligible && !!trackWithDefaultStage("flights_web_cat_fare_calendar_oneway", 1),
                        currencyCode
                    } }, verticalCollapsibleSearchbox))),
        React.createElement(HiddenVisually, null,
            React.createElement("h2", null, i18n.trans(t("a11y_flights_sr__results_hidden_heading")))),
        hideToolbar ? null : React.createElement(SearchToolbar, null)));
};
export default SearchHeader;
